/**
 * Der postMessage-Kanal zur einbettenden Seite.
 *
 * WARUM ES DIESEN KANAL BRAUCHT
 * -----------------------------
 * Eine angeheftete Warenkorb-Leiste kann NICHT im iframe leben.
 *
 * Gescrollt wird die einbettende Seite, nicht das iframe-Dokument.
 * `position: sticky` braucht aber einen Scrollport, und `position: fixed`
 * bezieht sich im iframe auf die iframe-Box statt auf das Browserfenster.
 * Beides landet damit am unteren Ende des iframes statt im Blickfeld.
 *
 * Gemessen (Chrome, iframe 8100px hoch, Elternseite auf y=3000 gescrollt):
 * eine Leiste mit `position:fixed; bottom:0` im iframe liegt bei y=8040 im
 * iframe-Dokument, also rund 7000px unterhalb des sichtbaren Bereichs.
 * `scrollTop` des iframe-Dokuments bleibt dabei konstant 0 — es scrollt
 * wirklich nie. Oben angeheftet ist es dasselbe Bild: die Leiste wandert im
 * Gleichschritt mit der Seite aus dem Bild.
 *
 * Deshalb rendert die Leiste die einbettende Seite (embed.js), und der Shop
 * schickt ihr nur den Warenkorbstand. Der Klick auf "Weiter" kommt auf
 * demselben Weg zurück und löst hier denselben Pfad aus wie der Button in
 * der Ticketliste.
 *
 * `window.parent`, nicht `window.top`: embed.js läuft in der DIREKTEN
 * Elternseite. Bei mehrfach verschachtelten iframes ist `top` ein anderes
 * Fenster, das gar nicht zuhört.
 */

/**
 * Läuft der Shop in einer fremden Seite? Nur dann gibt es einen Empfänger.
 */
export function isEmbedded() {
    return window.parent !== window;
}

/**
 * Einen Zustand nach draußen geben. Ohne Einbettung ein No-Op, damit
 * Aufrufer nicht selbst prüfen müssen.
 */
export function sendToParent(payload) {
    if (!isEmbedded()) return;
    window.parent.postMessage(payload, '*');
}

/**
 * Auf ein Kommando der Elternseite hören.
 *
 * @gotcha Die Herkunftsprüfung ist Pflicht, nicht Kosmetik: ohne sie könnte
 *   jedes Fenster, das eine Referenz auf dieses hier bekommt, den Shop
 *   weiterschalten. `event.source === window.parent` lässt genau das Fenster
 *   durch, das das iframe eingebettet hat — die Herkunft der Elternseite
 *   kennt der Shop nicht, ihre Fensterreferenz aber schon.
 *
 * @param {string} name    Schlüssel, auf den gehört wird (z.B. 'cartNext')
 * @param {Function} handler
 * @returns {Function} Abmelder — im unmounted-Hook aufrufen
 */
export function onParentCommand(name, handler) {
    const listener = (event) => {
        if (event.source !== window.parent) return;
        if (!event.data || typeof event.data !== 'object') return;
        if (!event.data[name]) return;
        handler(event.data);
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
}
